import { MeasureData } from "../interfaces";
import { CompositionClient, fatAndLean } from "./composition";

export interface ScanSide {
    id: string;
    date: Date;
    weightKg: number;
    fatPercent: number;
    fatKg: number;
    leanKg: number;
    waistCm?: number;
}

export interface ScanComparison {
    /** The earlier scan, whichever order they were picked in. */
    earlier: ScanSide;
    later: ScanSide;
    weightKgDelta: number;
    fatKgDelta: number;
    leanKgDelta: number;
    fatPercentDelta: number;
    /** Undefined when either scan is missing a waist by the client's own definition. */
    waistCmDelta?: number;
}

export interface ComparisonClient extends CompositionClient {
    /** Picks which waist field to read: the two are different measurements. */
    gender: "male" | "female";
}

/**
 * Two scans placed side by side, in chronological order.
 *
 * Ordering is by date rather than by pick order, so "earlier" always means
 * earlier — a coach tapping the newer scan first should not see the deltas
 * inverted.
 */
export const buildScanComparison = (
    measurements: MeasureData[],
    clientData: ComparisonClient,
    idA: string,
    idB: string
): ScanComparison | null => {
    if (idA === idB) return null;

    const gender = clientData.gender === "female" ? "female" : "male";

    const sideOf = (m: MeasureData): ScanSide | null => {
        // Fat and lean need only weight and body fat. Going through the muscle
        // estimate also demanded an age, so a client created without one had
        // no comparison at all.
        const res = fatAndLean(m.measurements.weight, m.measurements.body_fat);
        if (!res) return null;

        // A male waist is taken over the navel, a female waist is the narrowest
        // circumference. Reading the wrong field compares two different things.
        const waist =
            gender === "female"
                ? m.measurements.waist_female
                : m.measurements.waist;

        return {
            id: m.ID,
            date: new Date(m.createdAt),
            weightKg: res.fatKg + res.leanKg,
            fatPercent: res.fatPercent,
            fatKg: res.fatKg,
            leanKg: res.leanKg,
            waistCm: waist || undefined,
        };
    };

    const a = measurements.find((m) => m.ID === idA);
    const b = measurements.find((m) => m.ID === idB);
    if (!a || !b) return null;

    const sa = sideOf(a);
    const sb = sideOf(b);
    if (!sa || !sb) return null;

    const [earlier, later] =
        sa.date.getTime() <= sb.date.getTime() ? [sa, sb] : [sb, sa];

    return {
        earlier,
        later,
        weightKgDelta: later.weightKg - earlier.weightKg,
        fatKgDelta: later.fatKg - earlier.fatKg,
        leanKgDelta: later.leanKg - earlier.leanKg,
        fatPercentDelta: later.fatPercent - earlier.fatPercent,
        waistCmDelta:
            earlier.waistCm !== undefined && later.waistCm !== undefined
                ? later.waistCm - earlier.waistCm
                : undefined,
    };
};
